import { get, head } from 'lodash';
import { BACS_DEBIT_CODE, PAYMENTS_LIMIT } from '../../config';
import { formatBacsLimit, isUserPaymentAuthoriser } from '../../helpers/fieldHelpers';
import { getFromAccountById, getToAccountById, getAccountById, isCreditCard } from '../accounts/selectors';
import ApiConstants from '../../config/apiConstants';
import { getUserId } from '../user/selectors';
import { CONST_FILE_UPLOAD } from '../../config/constants';
import { getSelectedFromAccountDetails } from '../payeeSelector/selectors';

export const getToAccount = (state) => state.bacsDebit.toAccount;
export const getBacsPaymentName = (state) => state.bacsDebit.paymentName;
export const getSubmissionDate = (state) => state.bacsDebit.submissionDate;
export const getSettlementDate = (state) => state.bacsDebit.settlementDate;
export const getPayments = (state) => state.bacsDebit.payments;
export const getBacsId = (state) => state.bacsDebit.bacsId;
export const getBacsDebitPaymentId = (state) => get(state, 'bacsDebit.id', '');
export const isBacsDebitFileUploadBatch = (state) =>
	get(state, 'bacsDebit.batch_type', '') === CONST_FILE_UPLOAD.BATCH_TYPE;

export const getBacsTotal = (state) => {
	const { payments } = state.bacsDebit;
	const total = payments.reduce((accumulator, payment) => {
		const amount = parseFloat(get(payment, 'amount', '').replace(/,/g, ''));
		return accumulator + (Number.isNaN(amount) ? 0 : amount);
	}, 0);
	return total.toLocaleString('en-GB', { minimumFractionDigits: 2 });
};

export const getBacsDetails = (state) => ({ ...state.bacsDebit, total: getBacsTotal(state) });
export const hasMultiplePayments = (state) => state.bacsDebit.payments.length >= 2;
export const getBacsPaymentIds = (state) => state.bacsDebit.payments.map((payment) => payment.id);
export const getPaymentById = (state, id) => {
	const payment = state.bacsDebit.payments.find((payment) => payment.id === id);

	return { ...payment, isFromCreditCard: isCreditCard(state, payment.fromAccount) };
};

export const isNumberOfPaymentsLimit = ({ bacsDebit }) => bacsDebit.payments.length >= PAYMENTS_LIMIT;
export const getTotalLimitExceeded = (state) => get(state, 'bacsDebit.maxLimitExceeded', false);

export const getBacsLimit = (state) => {
	const accountId = getToAccount(state);
	const { bacs } = getAccountById(state, accountId) || { bacs: [] };
	const bacsId = getBacsId(state);
	const bacsInfo = bacs.find((bacsInfo) => bacsInfo && bacsInfo.type === BACS_DEBIT_CODE && bacsInfo.id === bacsId);
	return bacsInfo ? formatBacsLimit(bacsInfo) : null;
};

export const getBacsDetailsWithAccounts = (state) => {
	const { bacsDebit } = state;

	return {
		...bacsDebit,
		payments: bacsDebit.payments.map((payment) => ({ ...payment, from: getToAccountById(state, payment.fromAccount) })),
		total: getBacsTotal(state),
		to: getFromAccountById(state, state.bacsDebit.toAccount) || getSelectedFromAccountDetails(state),
	};
};

export const getRequiresAuthorisation = (state) =>
	get(state, 'bacsDebit.authorisation.status', '') === ApiConstants.obligate;

export const getConfirmationRequiresAuthorisation = (state) =>
	get(state, 'bacsDebit.confirmation.responseCode') === 401;

export const getApproverInfo = (state) => {
	const userList = get(state, 'bacsDebit.authorisation.user_list');
	const user = head(userList);
	if (user) {
		const { approver_name, approver_id } = user;
		return { approver_name, approver_id };
	}
	return null;
};

export const getBacsDeadlineMissed = (state) => state.bacsDebit.deadlineMissed;

export const getPaymentsToInternalCreditCards = (state) =>
	getPayments(state)
		.filter((payment) => isCreditCard(state, payment.fromAccount))
		.map((payment) => payment.id);

export const getFirstPaymentHasFromAccount = (state) => {
	if (state.bacsDebit.payments.length) return !!head(state.bacsDebit.payments).fromAccount;
	return false;
};

export const getBacsPaymentError = (state) => get(state, 'bacsDebit.confirmation.error');

export const getBacsDebitSingleAuthorisersList = (state) => get(state, 'bacsDebit.singleAuthoriserList', []);

export const getBacsDebitMultiAuthorisersList = (state) => get(state, 'bacsDebit.multiAuthoriserList', []);

export const getBacsDebitAuthorisers = (state) => get(state, 'bacsDebit.authorisers', []);

export const getAuthorisationType = (state) => {
	const userId = getUserId(state);
	const authorisers = getBacsDebitAuthorisers(state);

	if (!isUserPaymentAuthoriser(authorisers, userId)) {
		return 'THIRDPARTY';
	}

	return get(state, 'bacsDebit.confirmation.verification_type', '');
};

export const getBacsDebitIsDefaultAuthorisers = (state) => get(state, 'bacsDebit.isDefaultAuthorisers', false);

export const getBacsDebitImportedPayments = (state) => get(state, 'bacsDebit.importedPayments', []);
export const getSelectedBacsDebitPayment = (state) => {
	const payments = getPayments(state);
	const selectedPayment = payments.find(({ id }) => get(state, 'payeeSelector.selectedPaymentId') === id);
	const destinationAccount = getToAccount(state);
	if (!selectedPayment) return undefined;
	return { ...selectedPayment, destinationAccount };
};

export const getPaymentCount = (state) => getPayments(state).length;
export const isBacsDebitTRAObligate = (state) => get(state, 'bacsDebit.isTRAObligate', false);

export const getFromAccount = (state) => {
	const selectedPaymentId = get(state, 'payeeSelector.selectedPaymentId', '');
	if (selectedPaymentId) {
		const payment = getPayments(state).find(({ id }) => id === selectedPaymentId);
		return get(payment, 'fromAccount', '');
	}
	return '';
};

export const getBacsDebitSinglePaymentIdByToAccount = (state, toAccount) => {
	const paymentsList = getPayments(state);
	const payment = paymentsList.find((payment) => get(payment, 'toAccount') === toAccount);
	return get(payment, 'id', '');
};
